<script>
import Overlay from "@core/components/common/Overlay.vue";
import {SupplyChain} from "@core/api";
import backendResponse from "@core/mixins/ui/backend-response";
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import {BRow, BCol, BCard, BCardTitle} from 'bootstrap-vue'
import HamletTable from "@core/components/common/HamletTable.vue";
import {Button} from "@core/components/common";
import NoteModal from "@/views/supply-chain/supplier/NoteModal.vue";
import dateRenderer from "@core/mixins/date-renderer";
import StopButton from "@core/components/common/StopButton.vue";
import EmailModal from "@/views/supply-chain/supplier/EmailModal.vue";
import EmailCompose from "@/views/supply-chain/supplier/EmailCompose.vue";
import {FollowUpRecordType} from "@core/domain/supply-chain";
import {DocumentPreview} from "@core/components/supply-chain";
import downloadSupplyChainDocument from "@core/mixins/download-supply-chain-document";
import NoteType from "@/views/supply-chain/supplier/mixins/NoteType";

export default {
    mixins: [backendResponse, dateRenderer, downloadSupplyChainDocument, NoteType],
    components: {
        EmailModal, EmailCompose, StopButton, NoteModal, Button, HamletTable,
        Overlay, AppTimeline, AppTimelineItem, BRow, BCol, BCard, BCardTitle, DocumentPreview,
    },
    props: {
        id: {required: true}
    },
    data() {
        return {
            showLoading: true,

            fields: [
                {
                    key: 'dateTime',
                    label: this.$t('Component(Label):date'),
                    title: this.$t('Component(Label):date'),
                    order: 0,
                    active: true,
                },

                {
                    key: 'type',
                    label: this.$t('Component(Grid):column-type'),
                    title: this.$t('Component(Grid):column-type'),
                    order: 0,
                    active: true,
                },

                {
                    key: 'note',
                    label: this.$t('Component(Grid):column-follow-up-note'),
                    title: this.$t('Component(Grid):column-follow-up-note'),
                    order: 0,
                    active: true,
                    tdClass: 'wrap-spaces'
                },

                {
                    key: 'user',
                    label: this.$t('Component(Grid):column-user-details'),
                    title: this.$t('Component(Grid):column-user-details'),
                    order: 0,
                    active: true,
                },

                { key: 'actions', label: this.$t('Component(Grid):column-actions'), class: 'text-right' },
            ],

            followUp: {
                id: null,
                supplier: {
                    title: ''
                },
                records: []
            },

            editNote: null,
            canReply: false,
            showEmailHtml: false,
            showReplyModal: false,

            config: null,
            fetchCallback: null,

            attachment: null,
        }
    },
    methods: {
        removeRecord(record) {
            this.showLoading = true;
            SupplyChain
                .deleteFollowUpNote(this.followUp.id, record.id)
                .then(res => {
                    return this.fetchFollowUp();
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false);
        },
        onRowClick(record) {
            this.editNote = record;
            this.canReply = false;
            this.showEmailHtml = false;

            if (this.isNote(record)) {
                this.$refs.modal.show();
            } else if (this.isSupplierEmail(record)) {
                this.$refs.emailModal.show();
                this.canReply = true;
            } else if (this.isReplyToSupplierEmail(record)) {
                this.$refs.emailModal.show();
                this.showEmailHtml = true;
            }
        },
        onTableCreated(config, cb) {
            this.config = config;
            this.fetchCallback = cb;
        },
        fetch(config, cb) {
            const start = (config.page - 1) * config.perPage;
            cb(
                this.followUp.records.length,
                this.followUp.records.slice(start, start + config.perPage)
            );
        },
        addNote() {
            this.$refs.modal.show(true);
        },
        close() {
            this.$router.go(-1);
        },
        saveNote(data, record, hide, error) {
            this.showLoading = true;
            const isEdit = record && record.id;
            (isEdit
                ? SupplyChain.updateFollowUpNote(this.followUp.id, record.id, data.note, data.dateTime)
                : SupplyChain.newFollowUpNote(this.followUp.id, data.note, data.dateTime)
            ).then((r) => {
                hide();

                if (isEdit) {
                    return this.fetchFollowUp();
                } else {
                    this.followUp.records.unshift(r.data);
                    this.rerenderGrid();
                }
            }).catch(e => {
                this.handleBackendError(e);
                error();
            }).finally(() => this.showLoading = false);

            this.editNote = null;
        },
        rerenderGrid() {
            if (this.config) {
                this.fetch(this.config, this.fetchCallback);
            }
        },
        fetchFollowUp() {
            return SupplyChain
                .getFollowUpCard(this.id)
                .then(r => {
                    this.followUp = r.data;
                    this.rerenderGrid();
                })
                .catch(this.handleBackendError)
                .finally(() => this.showLoading = false)
        },
        showReplyForm() {
            this.showReplyModal = true;
        },
        discardReply() {
            this.showReplyModal = false;
        },
        reply(message, doneCallback) {
            SupplyChain
                .replyFollowUpEmail(this.editNote.id, message)
                .then(r => {
                    this.onReplySuccess();
                })
                .catch(e => {
                    this.handleBackendError(e);
                })
                .finally(doneCallback);
        },
        onReplySuccess() {
            this.showReplyModal = false;
            this.$nextTick(() => {
                this.$refs.emailModal.hide();
            });

            //  send reply is an asynchronous so lets wait for second
            setTimeout(() => {
                this.fetchFollowUp();
            }, 1000);
        },
        getRecordNote(record) {
            switch (record.type) {
                case FollowUpRecordType.NOTE:
                    return record.note;
                case FollowUpRecordType.SUPPLIER_EMAIL:
                    return record.attributes.shortText;
                case FollowUpRecordType.REPLY_TO_SUPPLIER_EMAIL:
                    return record.attributes.text;
            }

            return '';
        },
        previewAttachment(attachment) {
            this.attachment = attachment;
            this.$nextTick(() => {
                this.$refs.preview.show();
            });
        },
        onPreviewError(e) {
            this.$refs.preview.hide();
            this.handleBackendError(e);
        },
        downloadAttachment(attachment) {
            this
                .downloadDocument(attachment.id, attachment.name, 'supplier-email-attachment')
                .catch(this.handleBackendError);
        }
    },
    created() {
        this.fetchFollowUp();
    }
}
</script>

<template>
    <Overlay :show="showLoading">
        <DocumentPreview
            ref="preview"
            type="supplier-email-attachment"
            :file="attachment"
            @error="onPreviewError"
        />
        <NoteModal
            ref="modal"
            :record="editNote"
            @save="saveNote"
        />
        <EmailModal
            ref="emailModal"
            :record="editNote"
            :show-reply-button="canReply"
            :html="showEmailHtml"
            @view="previewAttachment"
            @download="downloadAttachment"
            @reply="showReplyForm"
        />
        <EmailCompose
            ref="replyModal"
            :shall-show-email-compose-modal="showReplyModal"
            @discard="discardReply"
            @reply="reply"
        />
        <b-card no-body>
            <div class="card-header pb-1">
                <div class="col-md-6 pl-0">
                    <b-card-title>
                        {{ $t('Page(FollowUpCard):Component(Text):label', {supplier: this.followUp.supplier.title}) }}
                    </b-card-title>
                </div>
                <div class="col-md-6 pr-0">
                    <div class="demo-inline-spacing float-right mr-0 pr-0">
                        <Button @click="close" class="mt-0 mr-0">
                            {{ $t('Component(Button):close') }}
                        </Button>
                    </div>
                </div>
            </div>
            <HamletTable
                ref="table"
                :columns="fields"
                :fetch="fetch"
                @created="onTableCreated"
                @onRowClick="onRowClick"
                :defaultPerPage="10"
            >
                <template #header-controls>
                    <Button
                        class="mt-0 btn-icon"
                        @click="addNote"
                    >
                        <feather-icon icon="PlusIcon" />
                    </Button>
                </template>

                <template #cell-dateTime="data">
                    {{ data.item.attributes.dateTime ? renderDateTime(data.item.attributes.dateTime) : renderDateTime(data.item.createdAt) }}
                </template>

                <template #cell-type="data">
                    {{ resolveType(data.item) }}
                    <span v-if="isSupplierEmail(data.item) && data.item.attributes.attachments.length > 0" class="ml-50">
                          <feather-icon
                              icon="PaperclipIcon"
                          />
                    </span>
                </template>

                <template #cell-note="data">
                    {{ getRecordNote(data.item) }}
                </template>

                <template #cell-user="data">
                    <span v-if="isNote(data.item)">{{ data.item.attributes.user.name }}</span>
                    <span v-else>-</span>
                </template>

                <template #cell-actions="data">
                    <div class="text-right" v-if="isNote(data.item)">
                        <Button
                            class="btn-icon mr-50"
                            @click="onRowClick(data.item)"
                        >
                            <feather-icon icon="Edit2Icon"/>
                        </Button>
                        <StopButton
                            class="btn-icon"
                            variant="danger"
                            @click="removeRecord(data.item)"
                        >
                            <feather-icon icon="Trash2Icon"/>
                        </StopButton>
                    </div>
                </template>
            </HamletTable>
        </b-card>
    </Overlay>
</template>
